import React, { useState, useEffect } from 'react';
import ApplicationScoring from '@/components/ApplicationScoring';
import { Button } from '@/components/ui/button';
import { Building2, FileText, Eye, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const AdminApplicationsTab = ({
  allApplications,
  committees,
  applicationFilter,
  setApplicationFilter,
  viewingApplication,
  setViewingApplication,
  scholarships,
  onScoreSubmit,
  setActiveTab
}) => {
  const [localViewingApplication, setLocalViewingApplication] = useState(null);

  useEffect(() => {
    if (!viewingApplication) {
      setLocalViewingApplication(null);
    }
  }, [viewingApplication]);

  return (
    <div className="bg-white rounded-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900">All Applications</h2>
          <p className="text-sm text-slate-500">View and manage applications across all committees</p>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={applicationFilter.committee_id || 'all'}
            onValueChange={(value) => setApplicationFilter({ ...applicationFilter, committee_id: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-48 rounded-sm">
              <SelectValue placeholder="Filter by committee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Committees</SelectItem>
              {committees.map((committee) => (
                <SelectItem key={committee.id} value={committee.id}>{committee.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={applicationFilter.status || 'all'}
            onValueChange={(value) => setApplicationFilter({ ...applicationFilter, status: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-40 rounded-sm">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="awarded">Awarded</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Applications grouped by committee */}
      {(() => {
        const filteredApps = allApplications.filter(app => {
          if (applicationFilter.committee_id && app.committee_id !== applicationFilter.committee_id) return false;
          if (applicationFilter.status && app.status !== applicationFilter.status) return false;
          return true;
        });

        const grouped = filteredApps.reduce((acc, app) => {
          const key = app.committee_name || 'Unassigned';
          if (!acc[key]) acc[key] = [];
          acc[key].push(app);
          return acc;
        }, {});

        const committeeNames = Object.keys(grouped).sort();

        if (filteredApps.length === 0) {
          return (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No applications found</p>
            </div>
          );
        }

        return (
          <>
            {committeeNames.map(committeeName => (
              <div key={committeeName} className="mb-8">
                <h3 className="font-heading text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-greek-blue" />
                  {committeeName}
                  <span className="text-sm font-normal text-slate-500">({grouped[committeeName].length} applications)</span>
                </h3>
                <div className="overflow-x-auto border border-slate-200 rounded-sm">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Applicant</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Scholarship</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Submitted</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {grouped[committeeName].map((app) => (
                        <tr key={app.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900">{app.applicant_name}</p>
                              <p className="text-sm text-slate-500">{app.applicant_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{app.scholarship_name}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              {
                                draft: 'bg-slate-100 text-slate-600',
                                submitted: 'bg-blue-100 text-blue-600',
                                under_review: 'bg-amber-100 text-amber-700',
                                awarded: 'bg-green-100 text-green-600',
                                rejected: 'bg-red-100 text-red-600'
                              }[app.status] || 'bg-slate-100 text-slate-600'
                            }`}>
                              {app.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-testid={`admin-application-score-${app.id}`}>
                            {app.score_summary?.has_scores && app.score_summary?.average_score !== null ? (
                              <div className="space-y-1">
                                <p className="text-sm font-semibold text-slate-900">
                                  {app.score_summary.average_score} / {app.score_summary.max_possible_score || 0}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {app.score_summary.completed_reviewers || 0} complete • {app.score_summary.total_reviewers || 0} total
                                </p>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400">No scores yet</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const selected = { ...app };
                                setLocalViewingApplication(selected);
                                setViewingApplication?.(selected);
                                setActiveTab?.('applications');
                              }}
                              className="text-greek-blue border-greek-blue/30 hover:bg-greek-blue/5 rounded-sm"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {localViewingApplication && (
              <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="admin-application-review-inline">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-900">Application Review</h3>
                    <p className="text-sm text-slate-500">Review responses and scoring for the selected applicant.</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLocalViewingApplication(null);
                      setViewingApplication?.(null);
                    }}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="close-application-review-inline"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <ApplicationScoring
                  application={localViewingApplication}
                  scholarship={scholarships.find(s => s.id === localViewingApplication.scholarship_id)}
                  onScoreSubmit={({ isComplete }) => {
                    onScoreSubmit?.();
                    if (isComplete) {
                      setLocalViewingApplication(null);
                      setViewingApplication?.(null);
                    }
                  }}
                />
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default AdminApplicationsTab;
